/* La Sura - paid cake extras as Shopify nested cart lines (loop 3a,
   DECISIONS 2026-09-27).

   An extra is a variant of the unlisted Cake extras product. Any ticked
   option whose card carries data-add-on="<variant id>" and whose input is
   tied to a product form (form="..." or data-extras-for="...") is added to
   the basket UNDER the cake, as its own line with its own price.

   How, as proven on the real store: one multipart request to /cart/add.js
   with items[0] = the cake (its id, quantity 1 and every properties[...]
   field, photos included) and items[1..] = the extras with parent_id set to
   the cake's variant. Horizon's own add sends only the cake, and Shopify
   ignores shopify-additional-items there, so when an extra is ticked this
   takes over the form's submit (window, capture phase, so it runs before
   Horizon's document-level handler), then fires the same cart:update event Horizon fires, which
   refreshes the basket drawer and count.

   Only when an extra is ticked: otherwise Horizon adds the cake as normal.
   The browser has already checked required fields, since submit only fires
   on a valid form. With no script the cake still adds, without extras. */
(() => {
  if (window.lasuraCakeExtras) return;
  window.lasuraCakeExtras = true;

  /* A classic deferred script like the builder's other scripts (DECISIONS 2026-09-27), so these
     three small helpers are kept here rather than imported from lasura-basket-utils.js, which
     the basket's ES modules share. Keep them in step with that file. */
  const root = () => window.Shopify?.routes?.root || '/';
  const cartSections = () =>
    [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean);
  const freshBuild = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;

  /* Paid choices are disabled in the markup so nobody can tick one that
     wouldn't be charged; this script is what charges them, so it enables them. */
  const enable = () =>
    document
      .querySelectorAll('[data-add-on] input[data-needs-js]')
      .forEach((input) => (input.disabled = false));
  enable();
  document.addEventListener('DOMContentLoaded', enable);
  /* Choices that follow the variant arrive again, disabled, after a size or finish change
     (assets/lasura-variant-swap.js). */
  document.addEventListener('lasura:variant-swapped', enable);

  /* One cake per basket line (Matt, 2026-09-27). Shopify merges two lines
     with the same variant and identical properties, so two identical builds
     would become one line at quantity 2. A hidden, unique _build property
     (Horizon hides names starting with "_") keeps every build on its own
     line; it's renewed after each add. */
  const stamp = () =>
    document.querySelectorAll('lasura-cake-builder[data-form-id]').forEach((builder) => {
      let input = builder.querySelector('[data-build-ref]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'properties[_build]';
        input.dataset.buildRef = '';
        input.setAttribute('form', builder.dataset.formId);
        builder.appendChild(input);
      }
      input.value = freshBuild();
    });
  stamp();
  document.addEventListener('DOMContentLoaded', stamp);
  document.addEventListener('cart:update', () => setTimeout(stamp, 0));

  /* The Extras block's own allergen line: what the ticked extras add beyond
     the product's declaration (the builder has its own). Natasha's Law, see
     flow.allergen_disclosure. */
  document.addEventListener('change', (event) => {
    const block = event.target.closest && event.target.closest('[data-extras-block]');
    if (!block) return;
    let base = [];
    try {
      base = JSON.parse(block.querySelector('[data-extras-base]').textContent) || [];
    } catch {}
    const added = new Set();
    block.querySelectorAll('[data-allergens]').forEach((card) => {
      /* A tick box's card, or a choice group's box (a radio in its label, redesign L1.4). */
      const on = card.querySelector('input:checked');
      if (on) card.dataset.allergens.split('|').forEach((a) => a && !base.includes(a) && added.add(a));
    });
    const line = block.querySelector('[data-extras-allergens]');
    line.hidden = added.size === 0;
    line.textContent = added.size ? `${line.dataset.label} ${[...added].join(', ')}.` : '';

    /* A choice group's per-item line (redesign L1.4): the picked box's price over its count. */
    const choice = event.target.closest('[data-extras-choice]');
    const each = choice?.querySelector('[data-extras-each]');
    if (each) {
      const value = choice.querySelector('input:checked')?.closest('[data-each]')?.dataset.each || '';
      each.hidden = !value;
      each.textContent = value ? each.dataset.template.replace('{each}', value) : '';
    }
  });

  /* Ticked cards, and the picked box of each choice group (Matching cupcakes; radios since redesign
     L1.4): both are labels carrying data-add-on, which is all the submit below reads. */
  const picked = (formId) => {
    const forForm = (el) => el.getAttribute('form') === formId || el.dataset.extrasFor === formId;
    return [...document.querySelectorAll('[data-add-on]')].filter((card) => {
      if (card.hidden || card.closest('[hidden]')) return false;
      const input = card.querySelector('input');
      if (!input || !input.checked) return false;
      return forForm(input);
    });
  };

  const showError = (form, message) => {
    let note = form.parentElement.querySelector('[data-extras-error]');
    if (!note) {
      note = document.createElement('p');
      note.className = 'lasura-extras-error';
      note.setAttribute('role', 'alert');
      note.dataset.extrasError = '';
      form.after(note);
    }
    note.textContent = message;
  };

  /* On window, capture phase: Horizon routes every submit through its own
     capture listener on document (component.js registerEventListeners), so a
     listener on document would run after it and the cake would be added twice.
     Window capture runs first, and stopPropagation keeps Horizon out. */
  window.addEventListener(
    'submit',
    async (event) => {
      const form = event.target;
      /* getAttribute, not form.id: Horizon's form has an input named "id",
         which shadows the form's own id property. */
      const formId = form instanceof HTMLFormElement ? form.getAttribute('id') : null;
      if (!formId || form.dataset.type !== 'add-to-cart-form') return;
      /* The design picker (assets/lasura-design-picker.js) holds the form while no design is
         picked, or while Horizon is still switching to the one just picked; it stops the submit. */
      if (form.dataset.lasuraHold) return;
      const extras = picked(formId);
      if (!extras.length) return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const fields = new FormData(form);
      const cakeId = fields.get('id');
      /* The form's own quantity, as Horizon's add would send; each cake gets its extras. */
      const quantity = Math.max(1, Math.floor(Number(fields.get('quantity')) || 1));
      const body = new FormData();
      body.append('items[0][id]', cakeId);
      body.append('items[0][quantity]', String(quantity));
      for (const [key, value] of fields) {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match) body.append(`items[0][properties][${match[1]}]`, value);
      }
      /* A card may carry its line's properties (data-add-on-props: a topper's words) and several
         lines (data-add-on-lines, [[variant, per cake]]: a candle per digit). */
      let n = 0, count = quantity;
      const json = (text, fallback) => {
        try {
          return JSON.parse(text) || fallback;
        } catch {
          return fallback;
        }
      };
      extras.forEach((card) => {
        const props = Object.entries(json(card.dataset.addOnProps, {})).filter(([, v]) => String(v).trim());
        const lines = card.dataset.addOnLines != null ? json(card.dataset.addOnLines, []) : [[card.dataset.addOn, 1]];
        lines.forEach(([id, per]) => {
          const each = quantity * Math.max(1, Math.floor(Number(per) || 1));
          n += 1;
          count += each;
          body.append(`items[${n}][id]`, id);
          body.append(`items[${n}][quantity]`, String(each));
          body.append(`items[${n}][parent_id]`, cakeId);
          props.forEach(([key, value]) => body.append(`items[${n}][properties][${key}]`, String(value).trim()));
        });
      });
      const sections = cartSections();
      if (sections.length) body.append('sections', sections.join(','));

      const url = typeof Theme !== 'undefined' && Theme.routes ? Theme.routes.cart_add_url : `${root()}cart/add.js`;
      const component = form.closest('product-form-component') || form;
      try {
        const response = await (
          await fetch(url, { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' }, body })
        ).json();
        if (response.status) return showError(form, response.description || response.message || '');
        form.parentElement.querySelector('[data-extras-error]')?.remove();
        const cart = await (await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } })).json();
        const update = new Event('cart:update', { bubbles: true });
        update.detail = {
          resource: cart,
          sourceId: String(cakeId),
          data: { source: 'product-form-component', itemCount: count, productId: component.dataset.productId, sections: response.sections }
        };
        component.dispatchEvent(update);
      } catch (error) {
        showError(form, String(error.message || error));
      }
    },
    true
  );
})();
